import { load } from 'recaptcha-v3'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import accountApi from '~/composables/api/accountApi'
import enquiryTravellerApi from '~/composables/api/enquiryTravellerApi'
import galleryApi from '~/composables/api/galleryApi'
import { DESKTOP } from '~/constant/common'
import { EnquiryOptionLevel, EnquiryOptionType } from '~/enum/enquiry'
import { assetCDN, isEmpty } from '~/helper'
import { getCookie } from '~/mixins/cookie'
import { useEnquiryCaptureStore } from '~/store/pinia/enquiryCapture'
import { useCurrenciesStore } from '~/store/pinia/currencies'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

export default function () {
  const route = useRoute()
  const { $logger } = useNuxtApp()
  const ASSETS_CDN = assetCDN()
  const currenciesStore = useCurrenciesStore()
  const enquiryCaptureStore = useEnquiryCaptureStore()
  const generalStore = useGeneralInfoStore()

  const apiLoaded = ref(false)
  const apiUserLoaded = ref(false)
  const dataDestination = ref([])
  const topInfo = reactive({
    title: `Design a Private Trip with our Experts for Free`,
    trip_name: '',
    rating: 91,
    bg: `${ASSETS_CDN}/images/enquiryB2C/mob_top_section_bg.jpg`,
    customers: 0,
  })
  const selectedCurrency = computed(() => {
    // return store.getters['currenciesStore/getCurrencySelected']
    return currenciesStore.getCurrencySelected
  })
  const windowWidth = computed(() => generalStore.generalData.windowWidth)
  const imgNameBanner = computed(() => {
    if (windowWidth.value > DESKTOP) return 'desk_'
    return 'tab_'
  })
  const userLogin = computed(() => enquiryCaptureStore.userLoginData)

  const desktopBanner = [`${ASSETS_CDN}/images/enquiryB2C/${imgNameBanner.value}02.jpg`, `${ASSETS_CDN}/images/enquiryB2C/${imgNameBanner.value}03.jpg`]
  const desktopFirstSlide = `${ASSETS_CDN}/images/enquiryB2C/${imgNameBanner.value}01.jpg`

  const mapParamsEnquiryOption = (params: any) => {
    switch (route.name) {
      case 'enquiry-submit-trip':
      case 'trip-plan':
        return {
          ...params,
          type: EnquiryOptionType.Gallery,
          trip_id: route.params.trip,
        }
      case 'enquiry-submit-destination':
        return {
          ...params,
          type: EnquiryOptionType.Country,
          country_url: route.params.galleryTypes,
        }
      case 'enquiry-submit-ld':
        return {
          ...params,
          type: EnquiryOptionType.Designer,
          slug: route.params.name,
        }
      default:
        return params
    }
  }
  const mapDataEnquiryOption = async (data: any) => {
    if (data.destinations) {
      dataDestination.value = data.destinations
    }

    switch (route.name) {
      case 'enquiry-submit-trip':
        desktopBanner.unshift(data?.feature_image?.url)
        topInfo.title = `Get A Free Quote Now`
        topInfo.trip_name = data?.trip_name
        topInfo.bg = data.feature_image?.url
        break
      case 'enquiry-submit-destination':
        if (data.feature_image) {
          topInfo.bg = data.feature_image?.url
          desktopBanner.unshift(topInfo.bg)
        } else {
          desktopBanner.unshift(desktopFirstSlide)
        }
        await getGalleryPage()
        break

      case 'enquiry-submit-ld':
        // dataStep.expert_id = data?.designer?.id
        if (data?.designer == null) {
          throw createError({
            statusCode: 404,
            fatal: false,
            message: 'Local Designer not found'
          })
        }

        topInfo.title = `Design a Private Trip with ${data?.designer?.name} for Free`
        topInfo.rating = (parseFloat(data?.designer?.rating) / 5) * 100
        topInfo.bg = data?.designer?.feature_image?.url
        topInfo.customers = data?.designer?.number_of_reviews
        desktopBanner.unshift(topInfo.bg)

        break

      default:
        return
    }
  }
  const fetchDataEnquiryOption = async () => {
    try {
      apiLoaded.value = route.name === 'enquiry-submit-global'
      let params = {
        level: EnquiryOptionLevel.Country,
        currency: selectedCurrency.value.code,
        full: 0,
      }
      const { data: res } = await enquiryTravellerApi.getOptionsEnquiryNew(mapParamsEnquiryOption(params))
      if (res.success) {
        enquiryCaptureStore.setEnquiryOptionData(res?.data)
        await mapDataEnquiryOption(res.data)
      } else {
        $logger.log(res?.message, {cause: {function: 'Fetch data enquiry option error'}})
      }
    } catch (e) {
      console.log(e)
      $logger.log(e, {cause: {function: 'Fetch data enquiry option error'}})
    } finally {
      generalStore.setLoading(false)
      apiLoaded.value = true
    }
  }
  const getDetailProfile = async () => {
    const is_verified = getCookie('is_verified=')
    if (is_verified === '1' && isEmpty(userLogin.value)) {
      apiUserLoaded.value = false
      try {
        const response = await accountApi.getDetailProfile(0)

        if (response?.data?.status === 200) {
          enquiryCaptureStore.setUserLoginData(response?.data?.data)
        } else {
          console.log(response?.data?.message)
          $logger.log(response?.data?.message, {cause: {function: 'Get detail profile error'}})
        }
      } catch (e) {
        console.log(e)
        $logger.log(e, {cause: {function: 'Get detail profile error'}})
      } finally {
        apiUserLoaded.value = true
      }
    } else {
      apiUserLoaded.value = true
    }
  }
  const getGalleryPage = async () => {
    try {
      let pageSettings = null
      let slug = `/${route.params.galleryTypes}-tours`

      if (process.client && (window as any).page_settings && (window as any).page_settings.hasOwnProperty(slug)) {
        pageSettings = (window as any).page_settings[slug]
      }

      if (!pageSettings) {
        pageSettings = (await galleryApi.getGalleryPage(slug))?.data.value?.data
        if (pageSettings && process.client) {
          if (!(window as any).page_settings) {
            ;(window as any).page_settings = {}
          }

          ;(window as any).page_settings[slug] = pageSettings
        }
      }

      const country = pageSettings?.filter_options?.country_ids
      let list: any[] = []

      country && country.forEach((code: string) => {
        let data = dataDestination.value?.find((item: any) => item.country_code === code)
        if (data) list.push(data)
      })

      enquiryCaptureStore.setGalleryData({ data: pageSettings, listDestination: list })
    } catch (e) {
      console.log(e)
      throw new Error('getGalleryPage error: ', { cause: e })
    } finally {
      apiLoaded.value = true
    }
  }
  const setRecaptcha = async () => {
    try {
      const recaptcha = await load(`${useAppConfig().siteKeyGreCaptcha}`)
      enquiryCaptureStore.setRecaptcha(recaptcha)
    } catch (e) {
      console.log(e)
      $logger.log(e, {cause: {function: 'Set recaptcha error'}})
    }
  }
  return {
    ASSETS_CDN,
    apiLoaded,
    apiUserLoaded,
    topInfo,
    desktopBanner,
    desktopFirstSlide,
    getDetailProfile,
    fetchDataEnquiryOption,
    setRecaptcha,
  }
}
